
import React from 'react';
import { Button } from '@/components/ui/button';
import { CheckCircle, Mail, Loader2, AlertTriangle, Download, User, Phone, School } from 'lucide-react';
import EmailErrorAlert from './EmailErrorAlert';

interface PaymentConfirmedProps {
  isSendingEmail: boolean;
  emailSendingError: string | null;
  retryEmailSending: () => Promise<void>;
  downloadQRCode: () => void;
  attendeeData?: any; // Adding attendee data to props
}

const PaymentConfirmed: React.FC<PaymentConfirmedProps> = ({
  isSendingEmail,
  emailSendingError,
  retryEmailSending,
  downloadQRCode,
  attendeeData
}) => {
  return (
    <div className="space-y-3">
      <Button 
        className="w-full" 
        variant="outline"
        disabled
      >
        <span className="flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-500" />
          Đã xác nhận thanh toán
        </span>
      </Button>
      
      {/* Display attendee information if available */}
      {attendeeData && (
        <div className="flex flex-col gap-2 py-3 px-4 bg-blue-50 border border-blue-100 rounded-md">
          <h3 className="font-medium text-blue-700 border-b border-blue-100 pb-2 mb-1">Thông tin đăng ký</h3>
          
          <div className="grid grid-cols-[24px_1fr] gap-x-2 gap-y-2 text-sm">
            <User className="h-4 w-4 text-blue-500" />
            <span className="text-gray-800">{attendeeData.name}</span>
            
            <Mail className="h-4 w-4 text-blue-500" />
            <span className="text-gray-800">{attendeeData.email}</span>
            
            <Phone className="h-4 w-4 text-blue-500" />
            <span className="text-gray-800">{attendeeData.phone}</span>
            
            <School className="h-4 w-4 text-blue-500" />
            <span className="text-gray-800">{attendeeData.class}</span>
          </div>
        </div>
      )}
      
      {isSendingEmail && (
        <div className="flex flex-col items-center justify-center gap-2 py-2 px-3 bg-blue-50 border border-blue-100 rounded-md">
          <div className="flex items-center gap-2 text-blue-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="font-medium">Đang gửi email xác nhận...</span>
          </div>
          <p className="text-xs text-blue-500">
            Vui lòng chờ trong giây lát. Email của bạn sẽ được gửi ngay lập tức.
          </p>
        </div>
      )}
      
      {!isSendingEmail && !emailSendingError && (
        <div className="flex flex-col items-center justify-center gap-2 py-2 px-3 bg-green-50 border border-green-100 rounded-md">
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircle className="h-4 w-4" />
            <span className="font-medium">Email xác nhận đã được gửi!</span>
          </div>
          <p className="text-xs text-green-500">
            Vui lòng kiểm tra hòm thư email của bạn. Trong trường hợp không nhận được, bạn có thể tải mã QR tại đây.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={downloadQRCode}
            className="mt-1 border-green-200 text-green-700 hover:bg-green-50 hover:text-green-800 flex items-center gap-1 text-xs"
          >
            <Download className="h-3 w-3" />
            Tải mã QR dự phòng
          </Button>
        </div>
      )}
      
      {emailSendingError && (
        <div className="mt-3">
          <EmailErrorAlert
            retryEmailSending={retryEmailSending}
            downloadQRCode={downloadQRCode}
          />
        </div>
      )}
    </div>
  );
};

export default PaymentConfirmed;
